import { useEffect, useId, useRef, useState, type CSSProperties, type ReactNode } from 'react'
import { useSwipeNavBlock } from '../hooks/useSwipeNavBlock'
import { DeckLanguageField } from './DeckLanguageField'

/**
 * The deck screen's controls on the screen-header bar (ADR-067, thirteenth
 * amendment): everything deck-scoped that used to sit in the band between the
 * bar and the two columns now sits in the bar itself, so the columns start
 * directly under it.
 *
 * All of it sits on the bar's own plane, which is charcoal in both themes
 * (DESIGN_SYSTEM.md §8), so it reads only `--header-*` / `--text-header*` /
 * `--control-solid-header*` tokens — never the page's.
 */

/** Hover delay before a hint shows. Focus shows it at once: a keyboard reader
 *  asked for this control, a pointer may only be passing over it. */
const HINT_HOVER_DELAY_MS = 350

/**
 * A hint under a bar control: the words a control that has shed its label (or
 * never had room for its explanation) still owes the reader. Shown on hover
 * AND on keyboard focus, and wired to the control by `aria-describedby`, so it
 * is not a mouse-only affordance the way a `title` attribute is.
 */
function useHint() {
  const id = useId()
  const [shown, setShown] = useState(false)
  const timer = useRef<number | null>(null)
  const clear = () => { if (timer.current !== null) { window.clearTimeout(timer.current); timer.current = null } }
  useEffect(() => clear, [])
  return {
    id,
    shown,
    handlers: {
      onMouseEnter: () => { clear(); timer.current = window.setTimeout(() => setShown(true), HINT_HOVER_DELAY_MS) },
      onMouseLeave: () => { clear(); setShown(false) },
      onFocus: () => { clear(); setShown(true) },
      onBlur: () => { clear(); setShown(false) },
    },
  }
}

function HintBubble({ id, shown, children }: { id: string; shown: boolean; children: ReactNode }) {
  // Always in the DOM, so `aria-describedby` resolves whether or not it is
  // painted; `visibility` rather than unmounting for the same reason.
  return (
    <span
      id={id}
      role="tooltip"
      style={{
        position: 'absolute', top: 'calc(100% + 8px)', right: 0, zIndex: 120,
        width: 'max-content', maxWidth: 280,
        padding: '8px 10px', borderRadius: 'var(--radius-sm)',
        background: 'var(--bg-surface)', border: '1px solid var(--border)', boxShadow: 'var(--shadow-float)',
        color: 'var(--text-primary)', fontFamily: 'var(--font-ui)', fontSize: 12.5, fontWeight: 400,
        lineHeight: 1.45, whiteSpace: 'normal', textAlign: 'left',
        visibility: shown ? 'visible' : 'hidden', pointerEvents: 'none',
      }}
    >
      {children}
    </span>
  )
}

const CAPSULE_BASE: CSSProperties = {
  display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 6, flexShrink: 0,
  height: 'var(--header-control-h)', boxSizing: 'border-box',
  borderRadius: 'var(--radius-pill)', padding: '0 12px',
  fontFamily: 'var(--font-ui)', fontSize: 12.5, fontWeight: 600, whiteSpace: 'nowrap',
  cursor: 'pointer',
}

/**
 * A bar button. Two weights:
 *
 *  - `plane` — the bordered capsule every bar action wears (§10.5): readable
 *    as pressable on the charcoal plane without claiming to be primary.
 *  - `solid` — `--control-solid-header`, the header plane's own solid ramp
 *    (§7's correction block). The screen's primary action takes it, because
 *    §10.5 keeps the accent fill out of the bar: primary by weight on this
 *    plane, never by accent.
 *
 * `iconOnly` sheds the label and keeps the icon, the accessible name and a
 * hint carrying the label — a rung of the bar's fit ladder, never a way to
 * ship a control without words.
 */
export function HeaderCapsule({
  label, icon, iconOnly = false, variant = 'plane', onClick, disabled = false, ariaLabel,
}: {
  label: string
  icon?: ReactNode
  iconOnly?: boolean
  variant?: 'plane' | 'solid'
  onClick: () => void
  disabled?: boolean
  ariaLabel?: string
}) {
  const hint = useHint()
  const solid = variant === 'solid'
  const rest: CSSProperties = solid
    ? { background: 'var(--control-solid-header)', color: 'var(--control-solid-header-text)', border: '1px solid var(--control-solid-header)' }
    : { background: 'var(--header-control-plane)', color: 'var(--text-header)', border: '1px solid var(--header-control-border)' }
  return (
    <span style={{ position: 'relative', display: 'inline-flex', flexShrink: 0 }}>
      <button
        onClick={disabled ? undefined : onClick}
        disabled={disabled}
        aria-label={ariaLabel ?? label}
        aria-describedby={iconOnly ? hint.id : undefined}
        style={{
          ...CAPSULE_BASE, ...rest,
          ...(iconOnly ? { width: 'var(--header-control-h)', padding: 0 } : {}),
          opacity: disabled ? 0.5 : 1, cursor: disabled ? 'default' : 'pointer',
        }}
        {...(iconOnly ? hint.handlers : {})}
        onMouseOver={(e) => {
          if (disabled) return
          const el = e.currentTarget
          if (solid) el.style.filter = 'brightness(0.94)'
          else { el.style.background = 'var(--header-control-plane-hover)'; el.style.borderColor = 'var(--header-control-border-hover)' }
        }}
        onMouseOut={(e) => {
          const el = e.currentTarget
          el.style.filter = ''
          el.style.background = rest.background as string
          el.style.borderColor = solid ? 'var(--control-solid-header)' : 'var(--header-control-border)'
        }}
      >
        {icon}
        {!iconOnly && label}
      </button>
      {iconOnly && <HintBubble id={hint.id} shown={hint.shown}>{label}</HintBubble>}
    </span>
  )
}

/**
 * The deck's daily-learning state, as a switch on the bar. It is a STATE the
 * reader reads at a glance, not only an action, which is why it never folds
 * into the overflow menu: at its tightest it drops its label and keeps the
 * switch (the design owner's decision on this pass). The explanation that used
 * to sit under the label in the band lives in the hint, shown on hover and on
 * focus. The accessible name is the label in every rung.
 *
 * The state is whatever `active` says — the page passes
 * `deck.is_active_for_learning`, the same field (and the same optimistic
 * `useToggleDeckLearning` mutation) every other surface reads.
 */
export function DeckDailySwitch({
  active, disabled, label, hint: hintText, showLabel, onToggle,
}: {
  active: boolean
  disabled: boolean
  label: string
  hint: string
  showLabel: boolean
  onToggle: () => void
}) {
  const hint = useHint()
  return (
    <span style={{ position: 'relative', display: 'inline-flex', flexShrink: 0 }}>
      <button
        type="button"
        role="switch"
        aria-checked={active}
        aria-label={label}
        aria-describedby={hint.id}
        data-deck-daily-switch
        onClick={disabled ? undefined : onToggle}
        disabled={disabled}
        {...hint.handlers}
        style={{
          display: 'inline-flex', alignItems: 'center', gap: 8, flexShrink: 0,
          height: 'var(--header-control-h)', boxSizing: 'border-box',
          padding: showLabel ? '0 10px 0 8px' : '0 8px', borderRadius: 'var(--radius-pill)',
          background: 'transparent', border: '1px solid transparent',
          color: 'var(--text-header)', fontFamily: 'var(--font-ui)', fontSize: 12.5, fontWeight: 500,
          whiteSpace: 'nowrap', cursor: disabled ? 'default' : 'pointer',
        }}
        onMouseOver={(e) => { if (!disabled) e.currentTarget.style.background = 'var(--header-control-hover)' }}
        onMouseOut={(e) => { e.currentTarget.style.background = 'transparent' }}
      >
        {/* The page's `LearningToggleSwitch` paints its off track in
            `--border`, a page token that nearly vanishes on the charcoal bar;
            this is the same switch drawn in the bar's own alphas. */}
        <span
          aria-hidden
          style={{
            width: 28, height: 16, borderRadius: 8, flexShrink: 0, position: 'relative',
            background: active ? 'var(--accent)' : 'var(--header-control-border-hover)',
            transition: 'background 0.2s',
          }}
        >
          <span style={{
            width: 12, height: 12, borderRadius: '50%', background: 'white',
            position: 'absolute', top: 2, left: active ? 14 : 2, transition: 'left 0.2s',
            boxShadow: '0 1px 3px rgba(0,0,0,0.25)',
          }} />
        </span>
        {showLabel && label}
      </button>
      <HintBubble id={hint.id} shown={hint.shown}>{hintText}</HintBubble>
    </span>
  )
}

/**
 * Edit details on desktop — the deck's name and description, in a dialog.
 *
 * The band used to be where these were edited in place; with the band gone
 * (ADR-067, thirteenth amendment) there is no in-flow place for a name field,
 * and the bar is the wrong plane for a text input that is not a search. One
 * save for both fields: the dialog is the one edit, so it commits once or not
 * at all. The phone keeps editing in place in its scrolling header.
 */
export function DeckDetailsDialog({
  initialTitle, initialDescription, language, labels, saving, onCancel, onSave,
}: {
  initialTitle: string
  initialDescription: string
  /** The deck's language, when the owner may change it here. */
  language?: { initial: string; labels: { label: string; note: string } }
  labels: { heading: string; name: string; description: string; descriptionPlaceholder: string; cancel: string; save: string }
  saving: boolean
  onCancel: () => void
  onSave: (title: string, description: string, language?: string) => void
}) {
  const [title, setTitle] = useState(initialTitle)
  const [description, setDescription] = useState(initialDescription)
  const [lang, setLang] = useState(language?.initial ?? '')
  const titleId = useId()
  const descId = useId()
  const headingId = useId()
  const canSave = title.trim().length > 0 && !saving
  const submit = () => { if (canSave) onSave(title.trim(), description.trim(), language ? lang : undefined) }
  // ADR-106 P3: mounted only while open.
  useSwipeNavBlock(true, 'deck-details-dialog')

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') { e.stopPropagation(); onCancel() } }
    window.addEventListener('keydown', onKey, true)
    return () => window.removeEventListener('keydown', onKey, true)
  }, [onCancel])

  const fieldStyle: CSSProperties = {
    width: '100%', boxSizing: 'border-box', padding: '8px 10px',
    background: 'var(--bg-secondary)', border: '1px solid var(--border)', borderRadius: 'var(--radius-sm)',
    color: 'var(--text-primary)', fontFamily: 'var(--font-ui)', fontSize: 14, outline: 'none',
  }
  const labelStyle: CSSProperties = { display: 'block', fontSize: 12.5, fontWeight: 600, color: 'var(--text-secondary)', margin: '0 0 6px' }

  return (
    <div
      onMouseDown={(e) => { if (e.target === e.currentTarget) onCancel() }}
      style={{ position: 'fixed', inset: 0, zIndex: 150, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}
    >
      <form
        role="dialog"
        aria-modal="true"
        aria-labelledby={headingId}
        onSubmit={(e) => { e.preventDefault(); submit() }}
        style={{ background: 'var(--bg-surface)', borderRadius: 'var(--radius-md)', boxShadow: 'var(--shadow-float)', padding: 24, width: '100%', maxWidth: 480 }}
      >
        <h3 id={headingId} style={{ margin: '0 0 16px', fontSize: 18, fontWeight: 700, color: 'var(--text-primary)' }}>{labels.heading}</h3>
        <label htmlFor={titleId} style={labelStyle}>{labels.name}</label>
        <input
          id={titleId}
          autoFocus
          value={title}
          maxLength={300}
          onChange={(e) => setTitle(e.target.value)}
          style={{ ...fieldStyle, marginBottom: 14, fontWeight: 600 }}
          onFocus={(e) => (e.currentTarget.style.borderColor = 'var(--accent)')}
          onBlur={(e) => (e.currentTarget.style.borderColor = 'var(--border)')}
        />
        <label htmlFor={descId} style={labelStyle}>{labels.description}</label>
        <textarea
          id={descId}
          value={description}
          rows={4}
          placeholder={labels.descriptionPlaceholder}
          onChange={(e) => setDescription(e.target.value)}
          style={{ ...fieldStyle, resize: 'vertical', lineHeight: 1.5 }}
          onFocus={(e) => (e.currentTarget.style.borderColor = 'var(--accent)')}
          onBlur={(e) => (e.currentTarget.style.borderColor = 'var(--border)')}
        />
        {language && (
          <DeckLanguageField
            value={lang}
            editable
            onChange={setLang}
            labels={language.labels}
          />
        )}
        <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end', marginTop: 20 }}>
          <button type="button" onClick={onCancel} style={{ padding: '9px 18px', background: 'var(--bg-secondary)', border: '1px solid var(--border)', borderRadius: 'var(--radius-pill)', fontSize: 14, cursor: 'pointer', color: 'var(--text-primary)' }}>{labels.cancel}</button>
          <button type="submit" disabled={!canSave} style={{ padding: '9px 18px', background: 'var(--accent-fill)', color: 'var(--text-on-accent)', border: 'none', borderRadius: 'var(--radius-pill)', fontSize: 14, fontWeight: 600, cursor: canSave ? 'pointer' : 'default', opacity: canSave ? 1 : 0.6 }}>{labels.save}</button>
        </div>
      </form>
    </div>
  )
}
